import { FMSHelper } from "@swivl-mono/helpers";
import {
  AccessCodeRequestResponse,
  AccessCodeRequestResponseStatus,
  FMSCreateLeadResponse,
  FMSError,
  FMSInterface,
  FMSPaymentInterface,
  FMSRoute,
  FMSServiceReturnData,
  FMSServiceReturnDataStatus,
  FMSType,
  FMSValidAuth,
  FacilityInterface,
  GetAccessCodeParams,
  GetLeadsParams,
  GetLeasesParams,
  GetTenantParams,
  GetTenantResponse,
  GetTenantsParams,
  GetUnitsParams,
  LocationVarKey,
  NormalizedFMSLeadInterface,
  NormalizedFMSLeaseInterface,
  NormalizedFMSTenantInterface,
  NormalizedFMSUnitInterface,
  NormalizedMakePaymentResponse,
  PaymentSessionStatus,
  RentalLead,
  UnitInterface,
} from "@swivl-mono/types";

import { API } from "../API";
import { APIService } from "../Service/API.Service";

// LINK apps/server/src/API/FMS/FMS.Controller.ts

export interface FMSServiceProviderInterface {
  // LINK libs/server-helpers/src/FMS/FMSs/FMS.Service.ts
  getUnits(fms: FMSType, auth: FMSValidAuth, params: GetUnitsParams): Promise<FMSServiceReturnData<NormalizedFMSUnitInterface[]>>;
  getTenants(fms: FMSType, auth: FMSValidAuth, params: GetTenantsParams): Promise<FMSServiceReturnData<NormalizedFMSTenantInterface[]>>;
  getLeases(fms: FMSType, auth: FMSValidAuth, params: GetLeasesParams): Promise<FMSServiceReturnData<NormalizedFMSLeaseInterface[]>>;
  getLeads(fms: FMSType, auth: FMSValidAuth, params: GetLeadsParams): Promise<FMSServiceReturnData<NormalizedFMSLeadInterface[]>>;
  createLead(
    fms: FMSType,
    auth: FMSValidAuth,
    lead: Partial<RentalLead>,
    facility: FacilityInterface,
  ): Promise<FMSServiceReturnData<FMSCreateLeadResponse>>;
  getTenant(fms: FMSType, auth: FMSValidAuth, params: GetTenantParams): Promise<FMSServiceReturnData<GetTenantResponse>>;
  getAccessCode(fms: FMSType, auth: FMSValidAuth, params: GetAccessCodeParams): Promise<FMSServiceReturnData<AccessCodeRequestResponse>>;
  makePayments(
    fms: FMSType,
    auth: FMSValidAuth,
    payment: FMSPaymentInterface,
    facilityId: string | undefined,
  ): Promise<FMSServiceReturnData<NormalizedMakePaymentResponse>>;
}

export class FMSAPI {
  static async getTenants(
    path: { fms: FMSType; facilityId: string },
    params: Record<string, any>,
    body: { auth: FMSValidAuth },
  ): Promise<FMSServiceReturnData<NormalizedFMSTenantInterface[]>> {
    const tenantsParams: GetTenantsParams = {
      ...(params ? params : {}),
      facilityId: path.facilityId,
    };

    let tenantsResponse: FMSServiceReturnData<NormalizedFMSTenantInterface[]>;
    try {
      tenantsResponse = await APIService.FMSServiceProvider.getTenants(path.fms, body?.auth, tenantsParams);
    } catch (e) {
      tenantsResponse = {
        error: {
          message: (e as FMSError)?.message,
          status: (e as FMSError)?.status,
        },
      };
    }

    return tenantsResponse;
  }

  static async getUnits(
    path: { fms: FMSType; facilityId: string },
    params: Record<string, any>,
    body: { auth: FMSValidAuth; priceField?: string; includeTax?: boolean }, // TODO: Temp until we get fms service moved over to mono
  ): Promise<FMSServiceReturnData<NormalizedFMSUnitInterface[]>> {
    const { priceField, includeTax } = body;
    const unitsParams: GetUnitsParams = {
      ...(params ? params : {}),
      priceField,
      includeTax,
      facilityId: path.facilityId,
    };

    let unitsResponse: FMSServiceReturnData<NormalizedFMSUnitInterface[]>;

    try {
      unitsResponse = await APIService.FMSServiceProvider.getUnits(path.fms, body?.auth, unitsParams);
    } catch (e) {
      unitsResponse = {
        error: {
          message: (e as FMSError)?.message,
          status: (e as FMSError)?.status,
        },
      };
    }

    return unitsResponse;
  }

  static async getUnitsDB(orgId: number, locationId: number, params?: { available?: boolean }): Promise<UnitInterface[]> {
    const units = await API.Units.getUnitsByLocationId(orgId, locationId, params);
    const sortedUnits: UnitInterface[] = FMSHelper.sortUnits(units);
    return sortedUnits;
  }

  static async getLeases(
    path: { fms: FMSType; facilityId: string },
    params: Record<string, any>,
    body: { auth: FMSValidAuth },
  ): Promise<FMSServiceReturnData<NormalizedFMSLeaseInterface[]>> {
    const leasesParams: GetLeasesParams = {
      ...(params ? params : {}),
      facilityId: path.facilityId,
    };

    let leasesResponse: FMSServiceReturnData<NormalizedFMSLeaseInterface[]>;

    try {
      leasesResponse = await APIService.FMSServiceProvider.getLeases(path.fms, body?.auth, leasesParams);
    } catch (e) {
      leasesResponse = {
        error: {
          message: (e as FMSError)?.message,
          status: (e as FMSError)?.status,
        },
      };
    }

    return leasesResponse;
  }

  static async getLeads(
    path: { fms: FMSType; facilityId: string },
    params: Record<string, any>,
    body: { auth: FMSValidAuth },
  ): Promise<FMSServiceReturnData<NormalizedFMSLeadInterface[]>> {
    const leadsParams: GetLeadsParams = {
      ...(params ? params : {}),
      facilityId: path.facilityId,
    };

    let leadsResponse: FMSServiceReturnData<NormalizedFMSLeadInterface[]>;

    try {
      leadsResponse = await APIService.FMSServiceProvider.getLeads(path.fms, body?.auth, leadsParams);
    } catch (e) {
      leadsResponse = {
        error: {
          message: (e as FMSError)?.message,
          status: (e as FMSError)?.status,
        },
      };
    }

    return leadsResponse;
  }

  static async createLead({
    fms,
    auth,
    lead,
    facility,
  }: {
    fms: FMSType;
    auth: FMSValidAuth;
    lead: Partial<RentalLead>;
    facility: FacilityInterface;
  }): Promise<FMSServiceReturnData<FMSCreateLeadResponse>> {
    if (!auth || !fms) {
      throw new Error("Missing createLead params");
    }

    let createLeadResponse: FMSServiceReturnData<FMSCreateLeadResponse> = {};

    try {
      createLeadResponse = await APIService.FMSServiceProvider.createLead(fms, auth, lead, facility);
    } catch (e) {
      console.warn("Error creating lead", e);
      createLeadResponse = {
        error: {
          message: (e as FMSError)?.message,
          status: (e as FMSError)?.status,
        },
        status: FMSServiceReturnDataStatus.FAILURE,
      };
    }

    return createLeadResponse;
  }

  static async getTenant({
    routeTo,
    liveParams,
    dbParams,
  }: {
    routeTo: FMSRoute;
    liveParams?: { fms: FMSType; auth: FMSValidAuth; params: GetTenantParams };
    dbParams?: {
      userId: number;
      locationId: number;
      identifiers?: { tenantId?: string; emails?: string[]; phoneNumbers?: string[] };
    };
  }): Promise<FMSServiceReturnData<GetTenantResponse>> {
    if (routeTo === FMSRoute.DATABASE) {
      // if(identifiers?.tenantId && locationId){
      //   return API.Tenants.getTenantByExternalTenantId(fms.orgId, locationId, identifiers.tenantId);
      // }
      // if(userId) {
      // }
    }

    if (routeTo === FMSRoute.LIVE) {
      if (!liveParams?.auth || !liveParams?.fms || !liveParams?.params.facilityId) {
        throw new Error("Missing getTenant params");
      }

      let tenantResponse: FMSServiceReturnData<GetTenantResponse>;

      try {
        tenantResponse = await APIService.FMSServiceProvider.getTenant(liveParams.fms, liveParams.auth, liveParams.params);
      } catch (e) {
        tenantResponse = {
          error: {
            message: (e as FMSError)?.message,
            status: (e as FMSError)?.status,
          },
        };
      }

      return tenantResponse;
    }

    return {};
  }

  static async getAccessCode({
    fms,
    auth,
    params,
  }: {
    fms: FMSType;
    auth: FMSValidAuth;
    params: GetAccessCodeParams;
  }): Promise<FMSServiceReturnData<AccessCodeRequestResponse>> {
    if (!auth || !fms) {
      throw new Error("Missing getAccessCode params");
    }

    let accessCodeResponse: FMSServiceReturnData<AccessCodeRequestResponse> = {};

    try {
      accessCodeResponse = await APIService.FMSServiceProvider.getAccessCode(fms, auth, params);
    } catch (e) {
      accessCodeResponse = {
        error: {
          message: (e as FMSError)?.message,
          status: (e as FMSError)?.status,
        },
      };
    }

    return accessCodeResponse;
  }

  static async makePayments(sessionId: string, signature: string, payment: FMSPaymentInterface): Promise<FMSServiceReturnData<NormalizedMakePaymentResponse>> {
    if (!sessionId) {
      throw new Error("Session ID is required to make a payment.");
    }
    let paymentSession = await API.Payments.getPaymentSession(sessionId);
    if (!paymentSession) {
      throw new Error("Payment session not found.");
    }
    paymentSession = await API.Payments.updatePaymentSession(sessionId, { paymentLink: null });

    if (paymentSession.status !== PaymentSessionStatus.OPENED) {
      throw new Error("Wrong payment session status.");
    }
    if (new Date(paymentSession.expiresAt) < new Date()) {
      throw new Error("Payment session has expired.");
    }
    if (!signature) {
      throw new Error("Signature is required to make a payment.");
    }
    const isSignatureValid = await API.Payments.verifyPaymentSessionSignature(sessionId, signature);
    if (!isSignatureValid) {
      throw new Error("Invalid signature.");
    }
    if (!payment.cardToken) {
      throw new Error("Card token is required to make a payment.");
    }
    const fms = await this.getFMSForLocation(paymentSession.orgId, paymentSession.locationId);
    if (!fms) {
      throw new Error("No FMS found for location");
    }

    const auth = fms.token as FMSValidAuth;
    const fmsType = fms.type as FMSType;
    const locationVars = await API.LocationVars.getVars([LocationVarKey.FMSId], paymentSession.orgId, undefined, paymentSession.locationId);
    const facilityId = locationVars[LocationVarKey.FMSId] || undefined;
    if (!facilityId) {
      throw new Error("No FMS facility ID found for location");
    }

    let makePaymentsResponse: FMSServiceReturnData<NormalizedMakePaymentResponse> = {};

    try {
      makePaymentsResponse = await APIService.FMSServiceProvider.makePayments(fmsType, auth, payment, facilityId);
      if (makePaymentsResponse?.status === FMSServiceReturnDataStatus.SUCCESS) {
        await API.Payments.closePaymentSession(sessionId, signature, PaymentSessionStatus.COMPLETED);
      }
    } catch (e) {
      console.error(e);
      makePaymentsResponse = {
        status: FMSServiceReturnDataStatus.FAILURE,
      };
    }

    return makePaymentsResponse;
  }

  static async getFMSByOrgId(orgId: number) {
    return APIService.Server.get<FMSInterface[]>(`/fms/org/${orgId}`);
  }

  static async getFMSForLocation(orgId: number, locationId?: number) {
    const payload = locationId ? { locationId: locationId } : undefined;
    return APIService.Server.get<FMSInterface>(`/fms/org/${orgId}/for-location`, payload);
  }

  static async getFMSByType(orgId: number, type: FMSType) {
    return APIService.Server.get<FMSInterface>(`/fms/type/${type}`);
  }

  static async getFMSById(orgId: number, fmsId: number) {
    return APIService.Server.get<FMSInterface>(`/fms/org/${orgId}/fms/${fmsId}`);
  }

  static async getSyncEnabledFMSs() {
    return APIService.Server.get<FMSInterface[]>(`/fms/sync-enabled`);
  }

  static async createFMS(orgId: number, fms: Partial<FMSInterface>) {
    return APIService.Server.post<FMSInterface>(`/fms/org/${orgId}/fms`, fms);
  }

  static async updateFMS(orgId: number, fmsId: number, fms: Partial<FMSInterface>) {
    return APIService.Server.patch<FMSInterface>(`/fms/org/${orgId}/fms/${fmsId}`, fms);
  }

  static async deleteFMS(orgId: number, fmsId: number) {
    return APIService.Server.delete<boolean>(`/fms/org/${orgId}/fms/${fmsId}`);
  }

  static async getAccessCodeByQueryId(
    orgId: number,
    locationId: number,
    fmsData: FMSInterface,
    facilityId: string,
    accessCodeQueryId: string,
  ): Promise<string | null> {
    const fms = fmsData.type as FMSType;
    const auth = fmsData.token as FMSValidAuth;
    const params: GetAccessCodeParams = { id: accessCodeQueryId, facilityId };

    if (!accessCodeQueryId) {
      return null;
    }

    const accessCodeResp = await FMSAPI.getAccessCode({ fms, auth, params });

    if (accessCodeResp?.items?.status === AccessCodeRequestResponseStatus.SUCCESS) {
      const accessCode: string | null = accessCodeResp?.items?.accessCode || null;

      if (accessCode) {
        return accessCode;
      }
    }

    return null;
  }

  static async getAccessCodesDB(orgId: number, userId: number): Promise<{ accessCodes: string[]; davinciCodes: string[] }> {
    const leases = await API.Leases.getLeasesByUserId(orgId, userId);
    if (leases?.length) {
      const accessCodes = leases.map((lease) => lease.accessCode).filter(Boolean) as string[];
      // const divinciCodes = leases.map(lease => lease.davinciCode);
      return { accessCodes, davinciCodes: [] };
    }
    return { accessCodes: [], davinciCodes: [] };
  }

}
